import type { FC } from 'react';

import { Button, RevelioTooltip } from '@revelio/ui';

import styles from './EditModeButtons.module.css';

interface EditModeButtonsProps {
  hasUnsavedChanges: boolean;
  isSaving: boolean;
  isNewPage?: boolean;
  isClone?: boolean;
  hasContent?: boolean;
  onSetViewMode: () => void;
  onDiscard: () => void;
  onSave: () => void;
}

export const EditModeButtons: FC<EditModeButtonsProps> = ({
  hasUnsavedChanges,
  isSaving,
  isNewPage = false,
  isClone = false,
  hasContent = false,
  onSetViewMode,
  onDiscard,
  onSave,
}) => {
  let cancelLabel = 'Cancel';
  let cancelOnClick: () => void = onSetViewMode;

  if (isNewPage) {
    cancelLabel = 'Cancel';
    cancelOnClick = onDiscard;
  } else if (hasUnsavedChanges) {
    cancelLabel = 'Discard changes';
    cancelOnClick = onDiscard;
  } else {
    cancelLabel = 'Cancel';
    cancelOnClick = onSetViewMode;
  }

  let saveLabel = 'Save';
  let saveTooltipLabel = '';
  let saveTooltipOffset = 0;
  let saveIsDisabled = !hasUnsavedChanges;

  if (isClone || isNewPage) {
    saveLabel = 'Create';
    saveTooltipLabel = 'Save as a new page';
    saveTooltipOffset = -35;
    saveIsDisabled = !hasContent;
  } else if (hasUnsavedChanges) {
    saveTooltipLabel = 'Save changes';
    saveIsDisabled = false;
  } else {
    saveTooltipLabel = 'There are no changes to save';
    saveTooltipOffset = -60;
  }

  return (
    <>
      <Button
        variant="ghost"
        color="secondary"
        shape="squared"
        size="md"
        onClick={cancelOnClick}
        disabled={isSaving}
        aria-label={cancelLabel}
        className={styles.cancel}
      >
        {cancelLabel}
      </Button>

      <RevelioTooltip label={saveTooltipLabel} crossOffset={saveTooltipOffset}>
        <span>
          <Button
            color="primary"
            shape="squared"
            size="md"
            onClick={onSave}
            disabled={saveIsDisabled || isSaving}
            aria-label={saveLabel}
          >
            {isSaving ? 'Saving' : saveLabel}
          </Button>
        </span>
      </RevelioTooltip>
    </>
  );
};
